import { ToggleCard } from '@ori-ui/components';
import { Add } from '@ori-ui/icons';
import type { SelectChangeEvent } from '@ori-ui/mui';
import { Typography, IconButton, styled, Skeleton } from '@ori-ui/mui';
import type { ApiModels } from '@ori/global-api-client';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { localization } from '../../constants';
import { stateContext, wizardContext } from '../../contexts';
import { useEndConsumers } from '../../hooks/useEndConsumers';
import type { CustomerSelectionProps } from '../../types';
import { EWizardCustomerType } from '../../types';
import { consultantSummaryToString } from '../../utils';
import { SelectField } from '../SelectField';
import { FirstName } from './FirstName';
import { Surname } from './Surname';

interface StyleProps {
  selected: boolean;
}

const StyledCard = styled(ToggleCard)<{ ownerState: StyleProps }>(({ theme, ownerState }) => ({
  padding: theme.spacing(3),
  paddingLeft: theme.spacing(4),
  border: `1px solid ${theme.palette.grey[300]}`,
  overflow: 'hidden',
  '&:hover': {
    [theme.breakpoints.up('md')]: {
      borderColor: theme.palette.grey[900],
    },
  },
  // !important to override normalize.css styles in online
  ...(ownerState.selected && { cursor: 'default !important' }),
}));

const NewCustomer = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(2),
  marginTop: theme.spacing(2),
  display: 'grid',
  rowGap: theme.spacing(3),
}));

const SelectedBoldTypography = styled(Typography)<{ ownerState: StyleProps }>(
  ({ theme, ownerState }) => ({
    ...(ownerState.selected && {
      fontWeight: 'bold',
      marginBottom: theme.spacing(2),
    }),
  }),
);

const SelectNewCustomer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `${theme.spacing(7)} auto`,
  gridTemplateAreas: `"new-customer-icon new-customer-label"`,
  paddingTop: theme.spacing(4),
  // fixing the weird gap in the green corner
  marginTop: -1,
}));

const AddIcon = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: '50%',
  gridArea: 'new-customer-icon',
  width: theme.spacing(6),
  height: theme.spacing(6),
}));

const SelectNewCustomerLabel = styled(Typography)(({ theme }) => ({
  gridArea: 'new-customer-label',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(1),
  cursor: 'pointer',
  marginRight: 'auto',
}));

const OtherCustomersSectionLoader = styled(Skeleton)({
  height: 53,
});

export const OtherCustomersSection = ({ selected, value, onChange }: CustomerSelectionProps) => {
  const { formatMessage } = useIntl();
  const { customerType } = useContext(wizardContext);
  const { existingCustomer, setExistingCustomer } = useContext(stateContext);
  const { data: endConsumersData, error } = useEndConsumers();
  const [selectedNewCustomer, setSelectedNewCustomer] = useState(false);
  const isLoading = !endConsumersData && !error;

  useEffect(() => {
    setSelectedNewCustomer(false);
  }, [selected]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      setSelectedNewCustomer(false);
      const customerToSave = endConsumersData?.collection.find(
        (consumer) => consultantSummaryToString(consumer) === event.target.value,
      );
      setExistingCustomer(customerToSave ?? null);
    },
    [endConsumersData?.collection, setExistingCustomer],
  );

  const selectNewCustomer = useCallback(() => {
    setExistingCustomer(null);
    setSelectedNewCustomer(true);
  }, [setExistingCustomer]);
  if (customerType === EWizardCustomerType.OnlyForMe) {
    return null;
  }

  if (isLoading) {
    return <OtherCustomersSectionLoader animation="wave" />;
  }

  const endConsumers: ApiModels.GlobalCustomersEndConsumersDto['collection'] =
    endConsumersData?.collection ?? [];

  const mapEndConsumers = endConsumers.map((consumer) => {
    const consumerName = consultantSummaryToString(consumer);

    return { Id: consumerName, Text: consumerName };
  });

  return (
    <StyledCard
      checked={selected}
      name="consultant-option"
      type="radio"
      id="existing-client"
      value={value}
      ownerState={{ selected }}
      onChange={onChange}
    >
      <SelectedBoldTypography ownerState={{ selected }}>
        <FormattedMessage
          id="customer"
          defaultMessage={localization.customer}
        />
      </SelectedBoldTypography>
      {selected && endConsumers.length > 0 ? (
        <SelectField
          displayEmpty
          id="select-customer"
          items={mapEndConsumers}
          placeholder={formatMessage({
            id: 'selectCustomer',
            defaultMessage: localization.selectCustomer,
          })}
          value={existingCustomer ? consultantSummaryToString(existingCustomer) : ''}
          onChange={handleChange}
        />
      ) : null}{' '}
      {selected && !selectedNewCustomer ? (
        <SelectNewCustomer tabIndex={0}>
          <AddIcon
            disableRipple
            size="large"
            onClick={selectNewCustomer}
            onKeyPress={selectNewCustomer}
          >
            <Add fontSize="small" />
          </AddIcon>
          <SelectNewCustomerLabel
            onClick={selectNewCustomer}
            onKeyPress={selectNewCustomer}
          >
            <FormattedMessage
              id="newCustomer"
              defaultMessage={localization.newCustomer}
            />
          </SelectNewCustomerLabel>
        </SelectNewCustomer>
      ) : null}
      {selected && selectedNewCustomer ? (
        <NewCustomer>
          <FirstName />
          <Surname />
        </NewCustomer>
      ) : null}
    </StyledCard>
  );
};
